import _ from 'lodash';
import Element from './Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import mixin from '../privates/utils/mixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import {getLinkObject, isEmptyArrayOfMarkers, transformMarkersFromData, isLocationObject, isNewGoogleMapsDataSchema, validateMarker, defaultMarkerData} from '../privates/utils/googleMapsUtils';
import {emptyStringIfNil} from '../privates/utils';

/**
 * @class GoogleMap
 * @summary A [Google Map element](https://www.wix.com/support/html5/article/adding-google-maps)
 *  that allows you to display a given location.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.GoogleMap
 * @definitionId wysiwyg.viewer.components.GoogleMap
 * @tagname GoogleMap
 */
class GoogleMap extends Element {

    /**
     * @typedef {external:Object} $w.GoogleMap~MapLocation
     * @syntax {latitude: 37.8, longitude: -122.4, description: "My Humble Home"}
     * @summary An object used by the `location` property that
     *  contains the attributes of a marked map location.
     * @property {external:Number} latitude The latitude of the location. Must be between -90 and 90.
     * @property {external:Number} longitude The longitude of the location. Must be between -180 and 180.
     * @property {external:String} [description] The description of the location.
     * @snippet [GoogleMap-location_get.es6=Get map location info]
     * @see [location](#location)
     */

  /**
   * @member location
   * @memberof $w.GoogleMap
   * @param {$w.GoogleMap~MapLocation} location
   */
    set location(location) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'location',
            dataToValidate: location,
            shouldCheckMandatoryFields: true,
            compName: this.id
        }) || !isLocationObject(location)) {
            return;
        }
        const description = emptyStringIfNil(location.description);
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        if (isNewGoogleMapsDataSchema(compData)) {
            compData.locations[0] = _.defaults({
                latitude: location.latitude,
                longitude: location.longitude,
                title: description
            }, compData.locations[0]);
            RMI.setData(compId, {
                locations: compData.locations
            });
            return;
        }
        RMI.setData(compId, {
            latitude: location.latitude,
            longitude: location.longitude,
            addressInfo: description
        });
    }

  /**
   * @member location
   * @label property
   * @syntax
   * get location(): MapLocation
   * set location(value: MapLocation): void
   * @summary Sets or gets the location information of a map's marked location.
   * @description
   *  Setting the `location` property to a [`MapLocation`](#MapLocation)
   *  object moves the map's marked location.
   *
   *  Getting the `location` property returns the [location
   *  information](#MapLocation) of the map's marked location.
   * @snippet [GoogleMap-location_get.es6=Get map location info]
   * @snippet [GoogleMap-location_set.es6=Set map location info]
   * @type {$w.GoogleMap~MapLocation}
   * @memberof $w.GoogleMap
   */
    get location() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        return isNewGoogleMapsDataSchema(compData) ? {
            latitude: compData.locations[0].latitude,
            longitude: compData.locations[0].longitude,
            description: compData.locations[0].title
        } : {
            latitude: compData.latitude,
            longitude: compData.longitude,
            description: compData.addressInfo
        };
    }

    /**
     * @member markers
     * @memberof $w.GoogleMap
     * @param {$w.GoogleMap~markers} markers
     */
    set markers(markers) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (isEmptyArrayOfMarkers(markers) || !_.every(markers, (marker, index) => validateMarker({
            compName: this.id,
            compType,
            marker,
            index
        }))) {
            return;
        }

        const locations = _.map(markers, (marker) => {
            return _.defaults({
            address: marker.address,
            latitude: _.get(marker, 'location.latitude'),
            longitude: _.get(marker, 'location.longitude'),
            title: marker.title,
            description: marker.description,
            link: _.isString(marker.link) ? getLinkObject(marker.link) : null,
            linkTitle: marker.linkTitle,
            pinIcon: marker.icon
            }, defaultMarkerData);
        });
        RMI.setData(compId, {
            locations
        });
    }

    /**
     * @member markers
     * @label property
     * @syntax
     * get markers(): get the array of locations in the map
     * @description
     *  Getting the `markers` property of the map
     * @type {$w.GoogleMap}
     * @memberof $w.GoogleMap
     */
    get markers() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        return transformMarkersFromData(compData.locations);
    }

    /**
     * @member center
     * @param {$w.GoogleMap~Center} center
     * @syntax
     * map.center set the center of the map
     * @description
     *  Setting the `center` property of the map
     * Example:
     * $w("#googleMap").setCenter({longitude: 50, latitude: 50})
     * @type {$w.GoogleMap}
     * @memberof $w.GoogleMap
     */
    setCenter(center) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'center',
            dataToValidate: center,
            shouldCheckMandatoryFields: true,
            compName: this.id
        }) || !isLocationObject(center)) {
            return;
        }
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            RMI.executeCompBehavior(compId, 'setMapCenter', {
                longitude: center.longitude,
                latitude: center.latitude
            }, () => {
                resolve();
            });
        });
    }

    /**
     * @member center
     * @syntax
     * map.center get the center of the map (latitude, longitude)
     * @description
     *  Getting the `center` property of the map
     * Example:
     * console.log($w("#googleMap").getCenter());
     * @type {$w.GoogleMap}
     * @memberof $w.GoogleMap
     */
    get center() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getState(compId).center;
    }

    /**
     * @member zoom
     * @memberof $w.GoogleMap
     * @param {external:Number} zoom
     */
    setZoom(zoom) {
        const compType = members._type.get(this).compType;
        if(!validate({
            compType,
            functionName: 'zoom',
            dataToValidate: {zoom},
            compName: this.id
        })) {
            return;
        }
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            RMI.executeCompBehavior(compId, 'setMapZoom', {zoom}, () => {
                resolve();
            });
        });
    }

    /**
     * @member zoom
     * @memberof $w.GoogleMap
     * @description
     *  Getting the zoom of the map
     */
    get zoom() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getState(compId).zoom;
    }

    /**
     * @function onMarkerClicked
     * @syntax
     * function onMarkerClicked(handler: EventHandler): GoogleMap
     * @summary Listens for map markers clicking.
     * @param {external:Function} callback with the marker's data as an argument
     * @description
     *  The `onMarkerClicked(callback)` invokes the argument function for each time a marker is clicked.
     *  The callback function receives 'markerData' object as an argument.
     * Example:
     *  $w("#googleMap").onMarkerClicked(markerData => {
     *      console.log(markerData.location);
     *      console.log(markerData.description);
     *  });
     * @eventtype onMarkerClicked
     * @eventhandler callback
     */
    onMarkerClicked(callback) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMarkerClicked',
            dataToValidate: {onMarkerClicked: callback},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().GOOGLE_MAP_MARKER_CLICKED, controllerId, callback);
        return this;
    }

    /**
     * @function onMapClicked
     * @syntax
     * function onMapClicked(handler: EventHandler): GoogleMap
     * @summary Listens for map clicking.
     * @param {external:Function} callback with the location data as an argument
     * @description
     *  The `onMapClicked(callback)` invokes the argument function for each time the map is clicked.
     *  The callback function receives 'clickLocation' object as an argument.
     * Example:
     *  $w("#googleMap").onMapClicked(clickLocation => {
     *      console.log(clickLocation.longitude);
     *      console.log(clickLocation.latitude);
     *  });
     * @eventtype onMapClicked
     * @eventhandler callback
     */
    onMapClicked(callback) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMapClicked',
            dataToValidate: {onMapClicked: callback},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().GOOGLE_MAP_CLICKED, controllerId, callback);
        return this;
    }

    /**
     * @function getVisibleMarkers
     * @syntax
     * function getVisibleMarkers()
     * @summary Gets visible markers for current map view.
     * @description
     *  The `getVisibleMarkers()` returns a promise with the markers
     * Example:
     *  $w("#googleMap").getVisibleMarkers().then(markers => {
     *      console.log(markers.length);
     *      console.log(markers[0].location);
     *      console.log(markers[0].description);
     *  });
     */
    getVisibleMarkers() {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            RMI.executeCompBehavior(compId, 'getVisibleMarkers', {}, (data) => {
                resolve(_.filter(data, _.isPlainObject));
            });
        });
    }

    toJSON() {
        const {id, type, global, rendered, location} = this;
        return {id, type, global, rendered, location};
    }
}

mixin(GoogleMap.prototype, HiddenCollapsedMixin.prototype);

export default GoogleMap;
